/**
 * Monotone cubic (Fritsch–Carlson) → SVG cubic Bézier.
 * The curve passes through every input point and does not add local extrema
 * between them, so monthly crossings stay the real samples (no fake peaks).
 */

export interface CurvePoint {
  x: number;
  y: number;
}

function fmt(n: number): string {
  return n.toFixed(2);
}

/** Tangents dy/dx that keep each segment monotone between its endpoints. */
export function monotoneTangents(points: CurvePoint[]): number[] {
  const n = points.length;
  const m = new Array<number>(n).fill(0);
  if (n < 2) return m;

  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    d.push(dx === 0 ? 0 : (points[i + 1].y - points[i].y) / dx);
  }

  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (d[i - 1] * d[i] <= 0) m[i] = 0;
    else m[i] = (d[i - 1] + d[i]) / 2;
  }

  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    if (a < 0) m[i] = 0;
    if (b < 0) m[i + 1] = 0;
    const aa = m[i] / d[i];
    const bb = m[i + 1] / d[i];
    const s = aa * aa + bb * bb;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * aa * d[i];
      m[i + 1] = t * bb * d[i];
    }
  }

  return m;
}

/** SVG path `d` through every point. One point is a move only (no invented segment). */
export function monotoneCubicPath(points: CurvePoint[]): string {
  if (!points.length) return '';
  let d = `M ${fmt(points[0].x)} ${fmt(points[0].y)}`;
  if (points.length === 1) return d;

  const m = monotoneTangents(points);
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const dx = p1.x - p0.x;
    const c1x = p0.x + dx / 3;
    const c1y = p0.y + (m[i] * dx) / 3;
    const c2x = p1.x - dx / 3;
    const c2y = p1.y - (m[i + 1] * dx) / 3;
    d += ` C ${fmt(c1x)} ${fmt(c1y)} ${fmt(c2x)} ${fmt(c2y)} ${fmt(p1.x)} ${fmt(p1.y)}`;
  }
  return d;
}

/** Closed area under the smooth line, down to baseY. Empty when fewer than 2 points. */
export function monotoneCubicArea(points: CurvePoint[], baseY: number): string {
  if (points.length < 2) return '';
  const last = points[points.length - 1];
  const first = points[0];
  return `${monotoneCubicPath(points)} L ${fmt(last.x)} ${fmt(baseY)} L ${fmt(first.x)} ${fmt(baseY)} Z`;
}
